"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, Download } from "lucide-react";
function InvestorMaterialsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="h-80 rounded-xl bg-muted/30 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-center text-muted-foreground">
        <p className="text-sm">Failed to load investor materials.</p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center text-muted-foreground">
        <p className="text-sm">No investor materials available at this time.</p>
      </div>;
  }
  const getTypeVariant = (type: string) => {
    switch (type) {
      case "Pitch Deck":
        return "default";
      case "Annual Report":
        return "secondary";
      case "Fact Sheet":
        return "outline";
      case "Technical Brief":
        return "destructive";
      default:
        return "outline";
    }
  };
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => {
      const title = post.customFields?.title || post.title;
      const type = post.customFields?.type || "";
      const pdfUrl = post.customFields?.pdf_url || "";
      return <Card key={post.id} data-index={i} className="group relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-medium">
            <CardHeader className="relative pb-4">
              <div className="absolute top-6 right-6 z-10">
                <Badge variant={getTypeVariant(type)} className="transition-all duration-200 group-hover:brightness-110">
                  {type}
                </Badge>
              </div>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="flex items-center justify-center h-32 bg-muted/20 rounded-lg">
                <FileText className="size-16 text-primary/40" />
              </div>

              <CardTitle className="text-base font-semibold text-foreground line-clamp-2 min-h-[3rem]">
                {title}
              </CardTitle>

              <div className="relative overflow-hidden">
                <Button variant="outline" className="w-full transition-all duration-300 group-hover:translate-x-0 translate-x-full opacity-0 group-hover:opacity-100" onClick={() => {
              if (pdfUrl) {
                window.open(pdfUrl, "_blank", "noopener,noreferrer");
              }
            }} disabled={!pdfUrl}>
                  <Download className="size-4" />
                  Download PDF
                </Button>
              </div>
            </CardContent>
          </Card>;
    })}
    </div>;
}
export default function InvestorMaterials() {
  return <section className="py-16 px-6 bg-background">
      <div className="container mx-auto max-w-7xl">
        <div className="mb-12 text-center space-y-4">
          <h2 className="text-4xl font-display font-bold text-foreground">
            Investor Materials
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Access our comprehensive collection of investor documents, including pitch decks, annual reports, fact sheets, and technical briefs.
          </p>
        </div>

        <PostsProvider postType="investor_material" wp_query={{
        per_page: 12,
        order: "desc",
        orderby: "date"
      }} embeds={[]} fields={["id", "title.rendered", "meta"]}>
          <InvestorMaterialsConsumer />
        </PostsProvider>
      </div>
    </section>;
}